(() => {
  const LEAFLET_VERSION = '1.9.4';
  const LEAFLET_SCRIPT = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;
  const LEAFLET_STYLES = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;

  const loadLeaflet = () => {
    if (window.L?.map) return Promise.resolve(window.L);
    if (window.scLeafletLoader) return window.scLeafletLoader;

    window.scLeafletLoader = new Promise((resolve, reject) => {
      if (!document.querySelector('link[data-sc-leaflet]')) {
        const stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.href = LEAFLET_STYLES;
        stylesheet.dataset.scLeaflet = '';
        document.head.appendChild(stylesheet);
      }

      const existingScript = document.querySelector('script[data-sc-leaflet]');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.L), { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = LEAFLET_SCRIPT;
      script.defer = true;
      script.dataset.scLeaflet = '';
      script.addEventListener('load', () => resolve(window.L), { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    return window.scLeafletLoader;
  };

  window.scLoadLeaflet = window.scLoadLeaflet || loadLeaflet;

  const normalise = (value = '') => value.toString().trim().toLowerCase();
  const unique = (values) => [...new Set(values.filter(Boolean))];
  const toCategories = (value) => {
    if (Array.isArray(value)) return unique(value.map((item) => item?.name || item).map((item) => item?.toString().trim()));
    return unique((value || '').toString().split(/[|,]/).map((item) => item.trim()));
  };
  const escapeAttribute = (value = '') => value.toString().replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  class SCLocationMap extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.canvas = this.querySelector('[data-location-map-canvas]');
      this.list = this.querySelector('.sc-location-map__list');
      this.cardTemplate = this.querySelector('[data-location-map-card-template]');
      this.searchForm = this.querySelector('[data-location-map-search-form]');
      this.searchInput = this.querySelector('[data-location-map-search]');
      this.searchButton = this.querySelector('[data-location-map-search-button]');
      this.filterSelect = this.querySelector('[data-location-map-filter]');
      this.filterWrap = this.querySelector('[data-location-map-filter-wrap]');
      this.countOutput = this.querySelector('[data-location-map-count]');
      this.emptyState = this.querySelector('[data-location-map-empty]');
      this.statusOutput = this.querySelector('[data-location-map-status]');
      this.pagination = this.querySelector('[data-location-map-pagination]');
      this.pageOutput = this.querySelector('[data-location-map-page]');
      this.previousButton = this.querySelector('[data-location-map-previous]');
      this.nextButton = this.querySelector('[data-location-map-next]');
      this.pageSize = Math.max(0, Number.parseInt(this.dataset.listPageSize, 10) || 0);
      this.currentPage = 1;
      this.searchMode = this.dataset.locationSearchMode || 'directory';
      this.externalAdapter = this.dataset.externalAdapter || 'none';
      this.searchLatitude = Number.parseFloat(this.dataset.centerLatitude) || 54.8;
      this.searchLongitude = Number.parseFloat(this.dataset.centerLongitude) || -3.4;

      this.bindInterface();
      this.start();
    }

    disconnectedCallback() {
      if (this.map) this.map.remove();
      if (this.blockSelectHandler) document.removeEventListener('shopify:block:select', this.blockSelectHandler);
    }

    async start() {
      try {
        if (this.externalAdapter === 'stockist') {
          this.setStatus(this.dataset.externalLoadingText, true);
          const records = await this.fetchStockistLocations(this.searchLatitude, this.searchLongitude);
          this.replaceExternalCards(records);
        } else if (this.externalAdapter === 'generic' && this.dataset.externalEndpoint) {
          this.setStatus(this.dataset.externalLoadingText, true);
          const records = await this.fetchGenericLocations();
          this.replaceExternalCards(records);
        }
        this.setStatus('', false);
      } catch (error) {
        this.setStatus(this.dataset.externalErrorText, true, true);
      }

      this.refreshLocations();
      this.classList.add('is-directory-ready');

      if (!this.canvas) return;

      loadLeaflet()
        .then((Leaflet) => this.createMap(Leaflet))
        .catch(() => {
          this.classList.add('has-map-error');
          const fallbackText = this.querySelector('.sc-location-map__fallback-text');
          if (fallbackText && this.dataset.mapErrorText) fallbackText.textContent = this.dataset.mapErrorText;
        });
    }

    readLocation(card, index) {
      const latitude = Number.parseFloat(card.dataset.latitude);
      const longitude = Number.parseFloat(card.dataset.longitude);
      const categories = toCategories(card.dataset.categories || card.dataset.category);

      return {
        index,
        card,
        source: card.dataset.source || 'manual',
        sourceKey: card.dataset.sourceKey || '',
        title: card.dataset.title || '',
        categories,
        region: card.dataset.region || '',
        address: card.dataset.address || '',
        detail: card.dataset.detail || '',
        image: card.dataset.image || '',
        link: card.dataset.link || '',
        linkLabel: card.dataset.linkLabel || '',
        directions: card.dataset.directions || '',
        latitude,
        longitude,
        hasCoordinates: Number.isFinite(latitude) && Number.isFinite(longitude),
        searchText: normalise(card.dataset.search),
        marker: null,
        visible: true
      };
    }

    refreshLocations(rebuildMarkers = false) {
      if (rebuildMarkers) this.clearMarkers();
      this.removeDuplicateCards();
      this.cards = [...this.querySelectorAll('[data-location-map-card]')];
      this.locations = this.cards.map((card, index) => this.readLocation(card, index));
      this.validLocations = this.locations.filter((location) => location.hasCoordinates);
      this.populateFilters();

      if (this.map) {
        this.addMarkers();
        this.enableLocationButtons();
      }

      this.updateResults();
    }

    removeDuplicateCards() {
      const seen = new Set();
      this.querySelectorAll('[data-location-map-card]').forEach((card) => {
        const key = normalise(card.dataset.sourceKey);
        if (!key) return;
        if (seen.has(key)) card.remove();
        else seen.add(key);
      });
    }

    populateFilters() {
      if (!this.filterSelect) return;
      const selectedCategory = this.filterSelect.value;
      while (this.filterSelect.options.length > 1) this.filterSelect.remove(1);

      let categories = unique(this.locations.flatMap((location) => location.categories));
      if (this.externalAdapter === 'stockist') {
        categories = unique([
          this.dataset.offTradeLabel || 'Bottle',
          this.dataset.onTradeLabel || 'Dram',
          ...categories
        ]);
      }
      categories.sort((a, b) => a.localeCompare(b));

      categories.forEach((category) => {
        const option = document.createElement('option');
        option.value = normalise(category);
        option.textContent = category;
        this.filterSelect.appendChild(option);
      });

      const selectedOption = [...this.filterSelect.options].find((option) => option.value === selectedCategory);
      this.filterSelect.value = selectedOption ? selectedCategory : '';

      if (this.filterWrap) this.filterWrap.hidden = categories.length < 2;
    }

    bindInterface() {
      this.previousButton?.addEventListener('click', () => this.showPage(this.currentPage - 1));
      this.nextButton?.addEventListener('click', () => this.showPage(this.currentPage + 1));
      this.searchInput?.addEventListener('input', () => {
        if (this.searchMode === 'directory') this.updateResults();
      });
      this.filterSelect?.addEventListener('change', () => {
        if (this.externalAdapter === 'stockist') this.refreshStockistFilter();
        else this.updateResults();
      });
      this.searchForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        if (this.searchMode === 'location') this.searchNearAddress();
        else this.updateResults();
      });

      this.list?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-location-map-select]');
        if (!button) return;
        const card = button.closest('[data-location-map-card]');
        const location = this.locations?.find((item) => item.card === card);
        if (location) this.activateLocation(location, true);
      });

      this.blockSelectHandler = (event) => {
        const blockId = event.detail?.blockId;
        if (!blockId) return;
        const location = this.locations?.find((item) => item.card.dataset.blockId === blockId);
        if (location) this.activateLocation(location, true);
      };
      document.addEventListener('shopify:block:select', this.blockSelectHandler);
    }

    async searchNearAddress() {
      const query = this.searchInput?.value.trim();
      if (!query || this.externalAdapter !== 'stockist') return;

      this.setLoading(true);
      this.setStatus(this.dataset.externalLoadingText, true);

      try {
        const result = await this.geocode(query);
        if (!result) {
          this.setStatus(this.dataset.addressNotFoundText, true, true);
          return;
        }

        this.searchLatitude = result.latitude;
        this.searchLongitude = result.longitude;
        const records = await this.fetchStockistLocations(
          result.latitude,
          result.longitude,
          this.getSelectedStockistSourceFilter()
        );
        this.replaceExternalCards(records);
        this.refreshLocations(true);
        if (this.map) {
          this.map.setView([result.latitude, result.longitude], 9);
          this.fitVisibleMarkers();
        }
        this.setStatus('', false);
      } catch (error) {
        this.setStatus(this.dataset.externalErrorText, true, true);
      } finally {
        this.setLoading(false);
      }
    }

    async refreshStockistFilter() {
      if (!Number.isFinite(this.searchLatitude) || !Number.isFinite(this.searchLongitude)) {
        this.updateResults();
        return;
      }

      this.setLoading(true);
      this.setStatus(this.dataset.externalLoadingText, true);

      try {
        const records = await this.fetchStockistLocations(
          this.searchLatitude,
          this.searchLongitude,
          this.getSelectedStockistSourceFilter()
        );
        this.replaceExternalCards(records);
        this.refreshLocations(true);
        this.setStatus('', false);
      } catch (error) {
        this.setStatus(this.dataset.externalErrorText, true, true);
      } finally {
        this.setLoading(false);
      }
    }

    getSelectedStockistSourceFilter() {
      const category = normalise(this.filterSelect?.value);
      if (category === normalise(this.dataset.offTradeLabel || 'Bottle')) {
        return this.dataset.offTradeSourceFilter || '';
      }
      if (category === normalise(this.dataset.onTradeLabel || 'Dram')) {
        return this.dataset.onTradeSourceFilter || '';
      }
      return '';
    }

    async geocode(query) {
      const endpoint = this.dataset.geocoderEndpoint || 'https://nominatim.openstreetmap.org/search';
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('format', 'jsonv2');
      url.searchParams.set('limit', '1');
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Geocoder unavailable');
      const results = await response.json();
      if (!Array.isArray(results) || !results.length) return null;
      const latitude = Number.parseFloat(results[0].lat);
      const longitude = Number.parseFloat(results[0].lon);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
      return { latitude, longitude };
    }

    async fetchStockistLocations(latitude, longitude, sourceFilter = '') {
      if (!this.dataset.externalEndpoint || !this.dataset.externalTag) return [];
      const url = new URL(this.dataset.externalEndpoint, window.location.origin);
      url.searchParams.set('tag', this.dataset.externalTag);
      url.searchParams.set('latitude', latitude);
      url.searchParams.set('longitude', longitude);
      url.searchParams.set('filter_operator', 'or');
      url.searchParams.set('distance', this.dataset.externalDistance || '50');
      if (sourceFilter) url.searchParams.set('filters', sourceFilter);
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Stockist feed unavailable');
      const payload = await response.json();
      return (payload.locations || []).map((record) => this.normaliseStockist(record));
    }

    normaliseStockist(record) {
      const filterNames = toCategories(record.filters || []);
      const onTradeFilter = normalise(this.dataset.onTradeFilter || 'Bar or Restaurant');
      const isDram = filterNames.some((name) => normalise(name) === onTradeFilter);
      const isBottle = filterNames.some((name) => normalise(name) !== onTradeFilter) || !isDram;
      const categories = [];
      if (isBottle) categories.push(this.dataset.offTradeLabel || 'Bottle');
      if (isDram) categories.push(this.dataset.onTradeLabel || 'Dram');
      const address = record.full_address || [record.address_line_1, record.address_line_2, record.city, record.state, record.postal_code, record.country].filter(Boolean).join(', ');
      const region = unique([record.city, record.state, record.country]).join(', ');
      const latitude = Number.parseFloat(record.latitude);
      const longitude = Number.parseFloat(record.longitude);
      const directions = Number.isFinite(latitude) && Number.isFinite(longitude)
        ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${latitude},${longitude}`)}`
        : '';

      return {
        sourceKey: `stockist:${record.id}`,
        recordKind: 'stockist',
        title: record.name || '',
        categories,
        region,
        address,
        detail: record.description || '',
        latitude,
        longitude,
        image: record.image_url || '',
        link: record.website || '',
        linkLabel: this.dataset.defaultLinkLabel || 'Visit website',
        directions
      };
    }

    async fetchGenericLocations() {
      const response = await fetch(this.dataset.externalEndpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('External feed unavailable');
      const payload = await response.json();
      const records = Array.isArray(payload) ? payload : payload.locations || payload.records || payload.items || payload.data || [];
      return records.map((record, index) => this.normaliseGeneric(record, index)).filter(Boolean);
    }

    normaliseGeneric(record, index) {
      if (!record || record.active === false || record.status === 'draft') return null;
      const coordinates = record.coordinates || {};
      return {
        sourceKey: record.source_key || record.sourceKey || record.id || `external:${index}`,
        recordKind: record.record_kind || record.recordKind || record.kind || '',
        title: record.title || record.name || '',
        categories: toCategories(record.categories || record.category || record.tags),
        region: record.region || record.town || record.city || '',
        address: record.address || record.location_label || record.locationLabel || '',
        detail: record.detail || record.summary || record.description || record.memory || '',
        latitude: record.latitude ?? record.lat ?? coordinates.latitude ?? coordinates.lat,
        longitude: record.longitude ?? record.lng ?? record.lon ?? coordinates.longitude ?? coordinates.lng ?? coordinates.lon,
        image: record.image_url || record.imageUrl || record.image || '',
        link: record.url || record.link || '',
        linkLabel: record.link_label || record.linkLabel || '',
        directions: record.directions_url || record.directionsUrl || ''
      };
    }

    replaceExternalCards(records) {
      if (!this.list || !this.cardTemplate) return;
      this.list.querySelectorAll('[data-location-map-card][data-source="external"]').forEach((card) => card.remove());
      const kindFilter = normalise(this.dataset.sourceKindFilter);

      records.forEach((record) => {
        if (!record || !record.title) return;
        if (kindFilter && normalise(record.recordKind) !== kindFilter) return;
        const fragment = this.cardTemplate.content.cloneNode(true);
        const card = fragment.querySelector('[data-location-map-card]');
        const categories = toCategories(record.categories);
        const latitude = Number.parseFloat(record.latitude);
        const longitude = Number.parseFloat(record.longitude);
        const search = [record.title, ...categories, record.region, record.address, record.detail].filter(Boolean).join(' ');

        card.dataset.sourceKey = record.sourceKey || '';
        card.dataset.title = record.title || '';
        card.dataset.categories = categories.join('|');
        card.dataset.region = record.region || '';
        card.dataset.address = record.address || '';
        card.dataset.detail = record.detail || '';
        card.dataset.latitude = Number.isFinite(latitude) ? latitude : '';
        card.dataset.longitude = Number.isFinite(longitude) ? longitude : '';
        card.dataset.image = record.image || '';
        card.dataset.link = record.link || '';
        card.dataset.linkLabel = record.linkLabel || '';
        card.dataset.directions = record.directions || '';
        card.dataset.search = search;

        const select = card.querySelector('[data-location-map-select]');
        select.setAttribute('aria-label', `${this.querySelector('.sc-location-map__cue')?.textContent || 'Show on map'}: ${record.title}`);
        card.querySelector('[data-location-map-title]').textContent = record.title;
        this.fillText(card.querySelector('[data-location-map-region]'), record.region);
        this.fillText(card.querySelector('[data-location-map-address]'), record.address);
        this.fillText(card.querySelector('[data-location-map-detail]'), record.detail);

        const categoryList = card.querySelector('[data-location-map-category-list]');
        categories.forEach((category) => {
          const label = document.createElement('span');
          label.className = 'sc-location-map__category';
          label.textContent = category;
          categoryList.appendChild(label);
        });
        if (!categories.length) categoryList.hidden = true;

        const image = card.querySelector('[data-location-map-image]');
        if (record.image) {
          image.src = record.image;
          image.alt = record.title;
          image.hidden = false;
        }

        const actions = card.querySelector('[data-location-map-actions]');
        const link = card.querySelector('[data-location-map-link]');
        const directions = card.querySelector('[data-location-map-directions]');
        if (record.link) {
          link.href = record.link;
          link.textContent = record.linkLabel || 'Visit website';
          link.hidden = false;
          actions.hidden = false;
        }
        if (record.directions) {
          directions.href = record.directions;
          directions.hidden = false;
          actions.hidden = false;
        }

        this.list.appendChild(fragment);
      });
    }

    fillText(element, value) {
      if (!element) return;
      element.textContent = value || '';
      element.hidden = !value;
    }

    createMap(Leaflet) {
      this.Leaflet = Leaflet;
      const centreLatitude = Number.parseFloat(this.dataset.centerLatitude) || 54.8;
      const centreLongitude = Number.parseFloat(this.dataset.centerLongitude) || -3.4;
      const initialZoom = Number.parseInt(this.dataset.initialZoom, 10) || 5;

      this.map = Leaflet.map(this.canvas, {
        center: [centreLatitude, centreLongitude],
        zoom: initialZoom,
        minZoom: 2,
        maxZoom: 18,
        scrollWheelZoom: this.dataset.scrollZoom === 'true',
        zoomControl: true,
        attributionControl: false,
        tap: true
      });

      Leaflet.tileLayer(this.dataset.tileUrl, { maxZoom: 19, subdomains: 'abc' }).addTo(this.map);
      this.addMarkers();
      this.enableLocationButtons();
      this.classList.add('is-map-ready');
      this.updateResults();

      requestAnimationFrame(() => {
        this.map.invalidateSize();
        if (this.dataset.fitMarkers === 'true') this.fitVisibleMarkers();
      });
    }

    addMarkers() {
      if (!this.map || !this.Leaflet) return;
      this.validLocations.forEach((location) => {
        if (location.marker) return;
        const marker = this.Leaflet.marker([location.latitude, location.longitude], {
          icon: this.createMarkerIcon(location),
          title: location.title,
          keyboard: true,
          riseOnHover: true
        });

        marker.bindTooltip(location.title, { direction: 'top', offset: [0, -16] });
        marker.bindPopup(this.createPopup(location), { offset: [0, -12], maxWidth: 300 });
        marker.on('click', () => this.activateLocation(location, false));
        marker.addTo(this.map);
        location.marker = marker;
      });
    }

    clearMarkers() {
      if (!this.map || !this.locations) return;
      this.locations.forEach((location) => {
        if (location.marker && this.map.hasLayer(location.marker)) this.map.removeLayer(location.marker);
      });
    }

    enableLocationButtons() {
      this.locations.forEach((location) => {
        const button = location.card.querySelector('[data-location-map-select]');
        if (button) button.disabled = !location.hasCoordinates;
      });
    }

    createMarkerIcon(location) {
      const markerSize = Number.parseFloat(getComputedStyle(this).getPropertyValue('--sc-location-map-marker-size')) || 32;
      const markerStyle = this.dataset.markerStyle || 'initials';
      const bottleLabel = normalise(this.dataset.offTradeLabel || 'Bottle');
      const dramLabel = normalise(this.dataset.onTradeLabel || 'Dram');
      const hasBottle = location.categories.some((category) => normalise(category) === bottleLabel);
      const hasDram = location.categories.some((category) => normalise(category) === dramLabel);
      let background = 'var(--sc-location-map-marker)';
      if (hasBottle && hasDram) background = 'conic-gradient(var(--sc-location-map-marker-bottle) 0 50%, var(--sc-location-map-marker-dram) 0)';
      else if (hasBottle) background = 'var(--sc-location-map-marker-bottle)';
      else if (hasDram) background = 'var(--sc-location-map-marker-dram)';

      let label = '';
      if (markerStyle === 'initials') {
        const labelSource = location.categories.length ? location.categories : location.title.split(/\s+/).slice(0, 2);
        label = labelSource.slice(0, 2).map((word) => word.charAt(0)).join('').toUpperCase();
      }

      let markerImage = this.dataset.markerImage || '';
      if (hasBottle && !hasDram && this.dataset.bottleMarkerImage) markerImage = this.dataset.bottleMarkerImage;
      if (hasDram && !hasBottle && this.dataset.dramMarkerImage) markerImage = this.dataset.dramMarkerImage;
      const imageMarkup = markerStyle === 'image' && markerImage
        ? `<img class="sc-location-map__marker-image" src="${escapeAttribute(markerImage)}" alt="">`
        : '';
      const labelMarkup = label ? `<span class="sc-location-map__marker-label" aria-hidden="true">${escapeAttribute(label)}</span>` : '';

      return this.Leaflet.divIcon({
        className: 'sc-location-map__marker-anchor',
        html: `<span class="sc-location-map__marker-wrap" style="--sc-location-marker-background:${background}">${imageMarkup}${labelMarkup}</span>`,
        iconSize: [markerSize, markerSize],
        iconAnchor: [markerSize / 2, markerSize / 2],
        popupAnchor: [0, -(markerSize / 2)]
      });
    }

    createPopup(location) {
      const popup = document.createElement('div');
      const title = document.createElement('strong');
      title.className = 'sc-location-map__popup-title';
      title.textContent = location.title;
      popup.appendChild(title);

      if (location.categories.length) {
        const category = document.createElement('p');
        category.className = 'sc-location-map__popup-category';
        category.textContent = location.categories.join(' / ');
        popup.appendChild(category);
      }

      const meta = [location.address, location.region].filter(Boolean).join(' - ');
      if (meta) {
        const paragraph = document.createElement('p');
        paragraph.className = 'sc-location-map__popup-meta';
        paragraph.textContent = meta;
        popup.appendChild(paragraph);
      }

      if (location.detail) {
        const detail = document.createElement('p');
        detail.className = 'sc-location-map__popup-detail';
        detail.textContent = location.detail;
        popup.appendChild(detail);
      }

      if (location.link) {
        const link = document.createElement('a');
        link.className = 'sc-location-map__popup-link';
        link.href = location.link;
        link.textContent = location.linkLabel || 'View details';
        popup.appendChild(link);
      }

      return popup;
    }

    updateResults() {
      if (!this.locations) return;
      const query = this.searchMode === 'directory' ? normalise(this.searchInput?.value) : '';
      const category = normalise(this.filterSelect?.value);
      let visibleCount = 0;

      this.locations.forEach((location) => {
        const matchesQuery = !query || location.searchText.includes(query);
        const matchesCategory = !category || location.categories.some((item) => normalise(item) === category);
        location.visible = matchesQuery && matchesCategory;

        if (location.visible) visibleCount += 1;

        if (location.marker && this.map) {
          if (location.visible && !this.map.hasLayer(location.marker)) location.marker.addTo(this.map);
          if (!location.visible && this.map.hasLayer(location.marker)) location.marker.removeFrom(this.map);
        }
      });

      if (this.countOutput) {
        const label = visibleCount === 1 ? this.dataset.singularLabel : this.dataset.pluralLabel;
        this.countOutput.textContent = `${visibleCount} ${label}`;
      }
      if (this.emptyState) this.emptyState.hidden = visibleCount > 0;

      this.showPage(1);
      if (this.map && this.dataset.fitOnFilter === 'true') this.fitVisibleMarkers();
    }

    showPage(page) {
      const matching = this.locations?.filter((location) => location.visible) || [];
      const totalPages = this.pageSize ? Math.max(1, Math.ceil(matching.length / this.pageSize)) : 1;
      this.currentPage = Math.max(1, Math.min(page, totalPages));
      const start = this.pageSize ? (this.currentPage - 1) * this.pageSize : 0;
      const pageLocations = new Set(this.pageSize ? matching.slice(start, start + this.pageSize) : matching);
      this.locations?.forEach((location) => { location.card.hidden = !pageLocations.has(location); });
      if (this.pagination) this.pagination.hidden = totalPages <= 1;
      if (this.previousButton) this.previousButton.disabled = this.currentPage === 1;
      if (this.nextButton) this.nextButton.disabled = this.currentPage === totalPages;
      if (this.pageOutput) {
        this.pageOutput.textContent = (this.dataset.pageLabel || '')
          .replace('__current__', this.currentPage)
          .replace('__total__', totalPages);
      }
    }

    fitVisibleMarkers() {
      const visible = this.validLocations?.filter((location) => location.visible && location.marker) || [];
      if (!visible.length || !this.map) return;

      if (visible.length === 1) {
        this.map.setView([visible[0].latitude, visible[0].longitude], Math.max(Number(this.dataset.initialZoom) || 5, 9));
        return;
      }

      const bounds = this.Leaflet.latLngBounds(visible.map((location) => [location.latitude, location.longitude]));
      this.map.fitBounds(bounds, { padding: [36, 36], maxZoom: 11 });
    }

    activateLocation(location, moveMap) {
      if (!location) return;
      if (this.pageSize && location.visible) {
        const index = this.locations.filter((item) => item.visible).indexOf(location);
        this.showPage(Math.floor(index / this.pageSize) + 1);
      }

      this.locations.forEach((item) => {
        const active = item === location;
        item.card.classList.toggle('is-active', active);
        item.card.querySelector('[data-location-map-select]')?.setAttribute('aria-pressed', active.toString());
        item.marker?.getElement()?.classList.toggle('is-active', active);
      });

      if (location.marker && this.map) {
        if (!this.map.hasLayer(location.marker)) location.marker.addTo(this.map);
        if (moveMap) this.map.flyTo([location.latitude, location.longitude], Math.max(this.map.getZoom(), 9), { duration: .55 });
        location.marker.openPopup();
      }
    }

    setStatus(message, visible, isError = false) {
      if (!this.statusOutput) return;
      this.statusOutput.textContent = message || '';
      this.statusOutput.hidden = !visible || !message;
      this.statusOutput.classList.toggle('is-error', isError);
    }

    setLoading(loading) {
      this.classList.toggle('is-searching', loading);
      if (this.searchButton) this.searchButton.disabled = loading;
      if (this.searchInput) this.searchInput.setAttribute('aria-busy', loading.toString());
    }
  }

  if (!customElements.get('sc-location-map')) customElements.define('sc-location-map', SCLocationMap);
})();
